import { useState, useEffect, useRef, useMemo, useCallback, FC } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  capitalizeWords,
  currencyFormatter,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { MdOutlineMap, MdOutlineTableRows } from 'react-icons/md';
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDevNonAdjusted
} from '@blisspointmedia/bpm-types/dist/brandHealthMetricsUtils';
import * as R from 'ramda';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { DatePicker } from '@Components/DatePicker';
import { DmaMap } from '@Components/DmaMap/DmaMap';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { IconSegmentedControlButton } from '@Components/SegmentedControlButton/IconSegmentedControlButton/IconSegmentedControlButton';
import { WidgetContainer } from '@Components/WidgetContainer';
import { ChartContainer } from '@Components/ChartContainer';
import '@Pages/GeoMMM/Results/GeographicAnalysis.scss';
import {
  BestAndWorstLegend,
  colorsWorst,
  colorsBest
} from '@Pages/GeoMMM/Results/Components/BestAndWorstLegend';
import { FilterCheckbox } from '@Pages/GeoMMM/Results/Components/FilterCheckbox';
import { GradientLegend } from '@Components/GradientLegend';
import { getGeoResponse, sortGeoEntriesByResponse } from '@Pages/GeoMMM/Results/geo-analysis-utils';
import {
  DMA_MAP_DEMO_COMPANY,
  GEO_MAP_MIN_HEIGHT,
  GEO_MAP_MIN_WIDTH
} from '@Pages/GeoMMM/geo-mmm-constants';
import { Background, Placement, Size } from '@Components/designConstants';
import { DateRange } from '../../../utils/date-utils';
import {
  Brand20,
  Brand30,
  Brand40,
  Brand50,
  Brand60,
  Brand70,
  Neutral300,
  ConvergentColorMap6 as GEO_MAP_COLORS
} from '../../../utils/colors';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { downloadPNG, exportToExcel } from '../../../utils/download-utils';

const BEST_AND_WORST_COLORS = [...colorsBest, ...colorsWorst];

const REVERSED_GEO_MAP_COLORS = [...GEO_MAP_COLORS].reverse();

const INSIGHT_LABELS = {
  revenue: {
    tooltip: 'Revenue',
    legend: 'Revenue'
  },
  kpiVolume: {
    tooltip: 'KPI Volume',
    legend: 'KPI Volume'
  },
  roas: {
    tooltip: 'ROAS',
    legend: 'ROAS'
  },
  cpa: {
    tooltip: 'Cost Per Acquisition',
    legend: 'CPA'
  },
  bestandworstroas: {
    tooltip: 'ROAS',
    legend: 'ROAS'
  },
  bestandworstcpa: {
    tooltip: 'Cost Per Acquisition',
    legend: 'CPA'
  }
};

const DATA_FORMATTERS = {
  revenue: (value: number) => currencyFormatter.format(value, 2),
  kpiVolume: (value: number) => numberFormatter.format(value, 2),
  roas: (value: number) => numberFormatter.format(value, 2),
  cpa: (value: number) => currencyFormatter.format(value, 2),
  bestandworstroas: (value: number) => numberFormatter.format(value, 2),
  bestandworstcpa: (value: number) => currencyFormatter.format(value, 2)
};

const INSIGHT_OPTIONS = {
  revenue: [
    { value: 'revenue', label: 'Revenue' },
    { value: 'roas', label: 'ROAS' },
    { value: 'bestandworstroas', label: 'Best and Worst ROAS' }
  ],
  kpiVolume: [
    { value: 'kpiVolume', label: 'KPI Volume' },
    { value: 'cpa', label: 'Cost Per Acquisition' },
    { value: 'bestandworstcpa', label: 'Best and Worst Cost Per Acquisition' }
  ]
};

const legendStyle = {
  background: `linear-gradient(90deg, ${Brand20} 0%, ${Brand20} 16.67%, ${Brand30} 16.67%, ${Brand30} 33.33%, ${Brand40} 33.33%, ${Brand40} 50%, ${Brand50} 50%, ${Brand50} 66.67%, ${Brand60} 66.67%, ${Brand60} 83.33%, ${Brand70} 83.33%, ${Brand70} 100%)`
};

type GeoRankMap = Record<
  string,
  {
    rank: number;
    dma: string;
    incrementalOutcome: number;
    spend: number;
    response: number;
  }
>;

interface GeographicAnalysisProps {
  groupBy: string;
  outcomeVariable: 'revenue' | 'kpiVolume';
  attributionByGeoRows: any[];
  geoNames: Record<string, string>;
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
}

export const GeographicAnalysis: FC<GeographicAnalysisProps> = ({
  groupBy,
  outcomeVariable,
  attributionByGeoRows,
  geoNames,
  channelBreakoutMap
}) => {
  const { cid: company } = useCompanyInfo();
  const mapSvgRef = useRef<SVGSVGElement | null>(null);
  const [gradientRange, setGradientRange] = useState<{ maxValue: number; minValue: number }>();
  const [insightsType, setInsightsType] = useState<string>(outcomeVariable);
  const [chartTypeView, setChartTypeView] = useState<string>('showMap');

  const displayOutcome = insightsType === 'revenue' || insightsType === 'kpiVolume';
  const displayBestAndWorst =
    insightsType === 'bestandworstroas' || insightsType === 'bestandworstcpa';
  const displayCpa = insightsType === 'bestandworstcpa' || insightsType === 'cpa';

  const [dateRange, setDateRange] = useState<DateRange>({
    start: '2023-01-02',
    end: '2024-12-23'
  });

  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);

  const getSegmentLabel = (breakout: any, groupByKey: string) => {
    if (!breakout) {
      return '';
    }
    if (groupByKey === 'channel') {
      return capitalizeWords(breakout.channel);
    }
    if (groupByKey === 'platform') {
      return capitalizeWords(
        `${breakout.channel}${breakout.platform ? `, ${breakout.platform}` : ''}`
      );
    }
    if (groupByKey === 'name') {
      const parts = [breakout.channel];
      if (breakout.platform) {
        parts.push(breakout.platform);
      }
      if (breakout.tactic) {
        parts.push(breakout.tactic);
      }
      if (breakout.brand_nonbrand) {
        parts.push(breakout.brand_nonbrand);
      }
      return capitalizeWords(parts.join(', '));
    }
    return '';
  };

  const { attributionByGeoData } = useMemo(() => {
    const attributionByGeoDataMap: Record<
      string,
      {
        dma: string;
        fullName: string;
        incrementalOutcome: number;
        spend: number;
        response: number;
      }
    > = {};
    if (!R.isEmpty(attributionByGeoRows)) {
      attributionByGeoRows.forEach(row => {
        const dma = row.geo.split('_')[1];

        // Baseline (Covariates) has zero spend and would inflate each DMA's ROAS.
        if (row.metric !== 'mean' || row.channel === 'Baseline' || !geoNames[dma]) {
          return;
        }

        const breakout = channelBreakoutMap[row.channel?.toLowerCase() || ''];
        const segmentLabel = getSegmentLabel(breakout, groupBy);

        // Filter out channel breakouts that are not selected
        if (
          insightsType !== 'bestandworstroas' &&
          selectedSegments.length >= 0 &&
          !selectedSegments.includes(segmentLabel)
        ) {
          return;
        }

        const { incremental_outcome: incrementalOutcome, spend } = row;
        if (!attributionByGeoDataMap[dma]) {
          attributionByGeoDataMap[dma] = {
            dma,
            fullName: geoNames[dma],
            incrementalOutcome: 0, // used for both revenue and kpiVolume
            spend: 0,
            response: 0 // used for both roi and cost per acquisition
          };
        }

        attributionByGeoDataMap[dma].incrementalOutcome += incrementalOutcome;
        attributionByGeoDataMap[dma].spend += spend;
      });

      // Calculate ROI/CPA for each DMA.
      R.keys(attributionByGeoDataMap).forEach(dma => {
        const { incrementalOutcome, spend } = attributionByGeoDataMap[dma];
        const response = getGeoResponse(incrementalOutcome, spend, displayCpa);
        attributionByGeoDataMap[dma].response = response;
      });
    }

    return {
      attributionByGeoData: attributionByGeoDataMap
    };
  }, [
    attributionByGeoRows,
    channelBreakoutMap,
    displayCpa,
    geoNames,
    groupBy,
    insightsType,
    selectedSegments
  ]);

  useEffect(() => {
    if (!R.isEmpty(R.keys(attributionByGeoData)) && !R.isEmpty(R.keys(geoNames))) {
      const insightValues: number[] = R.values(attributionByGeoData).map(obj =>
        displayOutcome ? obj.incrementalOutcome : obj.response
      );
      const minValue = Math.min(...insightValues);
      const maxValue = Math.max(...insightValues);
      setGradientRange({
        minValue: displayCpa ? maxValue : minValue,
        maxValue: displayCpa ? minValue : maxValue
      });
    }
  }, [attributionByGeoData, displayCpa, displayOutcome, geoNames, insightsType]);

  const { worstTenRanked, bestTenRanked } = useMemo<{
    worstTenRanked: GeoRankMap;
    bestTenRanked: GeoRankMap;
  }>(() => {
    if (!R.isEmpty(R.keys(attributionByGeoData))) {
      // Sort entries based on roas or cpa
      const entries = sortGeoEntriesByResponse(
        Object.entries(attributionByGeoData).map(([geo, data]) => ({ geo, ...data })),
        insightsType
      );

      const top10 = entries.slice(0, 10);
      const bottom10 = entries.slice(-10).reverse();

      const combinedTop = top10.map((item, index) => ({ ...item, rank: index + 1 }));
      const combinedBottom = bottom10.map((item, index) => ({ ...item, rank: index + 1 }));

      const topRanked = Object.fromEntries(combinedTop.map(({ geo, ...rest }) => [geo, rest]));
      const bottomRanked = Object.fromEntries(
        combinedBottom.map(({ geo, ...rest }) => [geo, rest])
      );

      return {
        worstTenRanked: bottomRanked,
        bestTenRanked: topRanked
      };
    }

    return {
      worstTenRanked: {},
      bestTenRanked: {}
    };
  }, [attributionByGeoData, insightsType]);

  const thresholds = getThresholdBinsWithStdDevNonAdjusted(
    Object.values(attributionByGeoData).map(item =>
      displayOutcome ? item.incrementalOutcome : item.response
    )
  );

  const { renderColor, renderToolTip } = useMemo(() => {
    if (!R.isEmpty(R.keys(attributionByGeoData)) && !R.isEmpty(R.keys(geoNames)) && gradientRange) {
      let topAndBottomRankedByGeo;
      if (displayBestAndWorst) {
        const entries = sortGeoEntriesByResponse(
          Object.entries(attributionByGeoData).map(([geo, data]) => ({ geo, ...data })),
          insightsType
        );

        const top10 = entries.slice(0, 10);
        const bottom10 = entries.slice(-10).reverse();

        const combined = [...top10, ...bottom10].map((item, index) => ({
          ...item,
          rank: index + 1
        }));

        topAndBottomRankedByGeo = Object.fromEntries(
          combined.map(({ geo, ...rest }) => [geo, rest])
        );
      }

      const computeColor = (dma_code: string) => {
        const { minValue, maxValue } = gradientRange;
        const range = Math.abs(maxValue - minValue);

        // Get the insights value for the given DMA code
        let insightsValue: number;
        if (displayOutcome) {
          insightsValue = attributionByGeoData[dma_code]?.incrementalOutcome;
        } else if (displayBestAndWorst) {
          insightsValue = topAndBottomRankedByGeo[dma_code]?.response;
        } else {
          insightsValue = attributionByGeoData[dma_code]?.response;
        }

        // If no response value or if all values are the same, return the first color
        if (insightsValue === undefined || range === 0) {
          return Neutral300;
        }

        if (displayBestAndWorst) {
          const rank = topAndBottomRankedByGeo[dma_code]?.rank;
          return rank === undefined ? Neutral300 : BEST_AND_WORST_COLORS[rank - 1];
        }
        return getColorForIndexedVolume(
          insightsValue,
          thresholds,
          displayCpa ? REVERSED_GEO_MAP_COLORS : GEO_MAP_COLORS
        );
      };

      const computeToolTip = (dma_code: string) => {
        if (
          !geoNames[dma_code] ||
          (displayBestAndWorst && !(dma_code in topAndBottomRankedByGeo))
        ) {
          // If the DMA code is not in geoNames or not in best and worst, hide tooltip
          return undefined;
        }

        const insightsValue = displayOutcome
          ? attributionByGeoData[dma_code]?.incrementalOutcome
          : attributionByGeoData[dma_code]?.response;

        if (!insightsValue) {
          let fieldValue = 'N/A';
          if (insightsType === 'revenue') {
            fieldValue = '$0';
          }
          if (insightsType === 'kpiVolume') {
            fieldValue = '0';
          }

          return (
            <div className='geo-map-tooltip'>
              <div className='geo-map-tooltip-header'>{`${
                geoNames[dma_code] ?? 'N/A'
              } (DMA Code: ${dma_code})`}</div>
              <div className='geo-map-tooltip-body'>
                <div className='field-name'>{INSIGHT_LABELS[insightsType].tooltip}</div>
                <div className='field-value'>{fieldValue}</div>
              </div>
            </div>
          );
        }

        const formattedInsightsValue = DATA_FORMATTERS[insightsType](insightsValue);

        return (
          <div className='geo-map-tooltip'>
            <div className='geo-map-tooltip-header'>{`${
              geoNames[dma_code] ?? 'N/A'
            } (DMA Code: ${dma_code})`}</div>
            <div className='geo-map-tooltip-body'>
              <div className='field-name'>{INSIGHT_LABELS[insightsType].tooltip}</div>
              <div className='field-value'>{formattedInsightsValue}</div>
            </div>
          </div>
        );
      };

      return {
        renderColor: computeColor,
        renderToolTip: computeToolTip
      };
    }
    return {
      renderColor: () => Neutral300,
      renderToolTip: () => 'No info for this DMA'
    };
  }, [
    attributionByGeoData,
    displayBestAndWorst,
    displayCpa,
    displayOutcome,
    geoNames,
    gradientRange,
    insightsType,
    thresholds
  ]);

  const filterOptions = useMemo(() => {
    const segments = Object.values(channelBreakoutMap).reduce<string[]>((acc, entry) => {
      const seg = getSegmentLabel(entry, groupBy);
      if (seg && !acc.includes(seg)) {
        acc.push(seg);
      }
      return acc;
    }, []);
    return segments;
  }, [channelBreakoutMap, groupBy]);

  // By default have all channel breakouts selected except for Linear TV
  useEffect(() => {
    if (filterOptions.length) {
      setSelectedSegments(filterOptions.filter(option => option !== 'Linear TV'));
    }
  }, [groupBy, filterOptions]);

  const insightOptions = useMemo(() => {
    const options = INSIGHT_OPTIONS[outcomeVariable];
    if (chartTypeView === 'showTable') {
      return options.filter(
        option => option.value !== 'bestandworstroas' && option.value !== 'bestandworstcpa'
      );
    }

    return options;
  }, [chartTypeView, outcomeVariable]);

  const tableHeaders = useMemo(
    () => [
      {
        name: 'fullName',
        label: 'DMA Full Name',
        flex: 1
      },
      {
        name: 'dma',
        label: 'DMA Code',
        flex: 1
      },
      {
        name: displayOutcome ? 'incrementalOutcome' : 'response',
        label: INSIGHT_LABELS[insightsType].tooltip,
        flex: 1
      },
      {
        name: 'spend',
        label: 'Spend',
        flex: 1
      }
    ],
    [displayOutcome, insightsType]
  );

  const pngDownload = useCallback(async () => {
    await downloadPNG('.geo-map-container', `${insightsType}_by_DMA`);
  }, [insightsType]);

  const excelDownload = useCallback(() => {
    if (displayBestAndWorst) {
      const data = Object.values(bestTenRanked)
        .sort((a, b) => a.rank - b.rank)
        .concat(Object.values(worstTenRanked).sort((a, b) => a.rank - b.rank));
      exportToExcel(data, `${insightsType}_by_DMA`);
    } else {
      exportToExcel(Object.values(attributionByGeoData), `${insightsType}_by_DMA`);
    }
  }, [attributionByGeoData, bestTenRanked, displayBestAndWorst, insightsType, worstTenRanked]);

  const gradientLegendFormatLabel = (value: number) => {
    if (insightsType === 'revenue' && value >= 1000000) {
      return currencyFormatter.format(value, 2);
    }
    if (insightsType === 'revenue') {
      return currencyFormatter.format(value, 0);
    }
    if (insightsType === 'kpiVolume' && value >= 1000000) {
      return numberFormatter.format(value, 2);
    }
    if (insightsType === 'kpiVolume') {
      return numberFormatter.format(value, 0);
    }
    return DATA_FORMATTERS[insightsType](value);
  };

  return (
    <div className='geographic-analysis-widget'>
      <WidgetContainer
        collapsible
        header='Geographic Analysis'
        subHeader='Below is the breakout of performance in each geography included in the model.'
      >
        <div className='geographic-analysis-container'>
          <ChartContainer
            rightActions={
              <div className='geography-analysis-right-actions'>
                <DatePicker
                  range={dateRange}
                  onChange={setDateRange}
                  background={Background.LIGHT}
                  disabled
                />
                <IconSegmentedControlButton
                  options={[
                    { key: 'showMap', icon: <MdOutlineMap />, label: 'map view' },
                    { key: 'showTable', icon: <MdOutlineTableRows />, label: 'table view' }
                  ]}
                  selectedOption={chartTypeView}
                  onChange={selected => setChartTypeView(selected)}
                  disabled={displayBestAndWorst}
                />
                <DownloadDropdown onClickOptions={[excelDownload, pngDownload]} />
              </div>
            }
            title={
              <>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  type={DropdownToggleType.WIDGET_TITLE}
                  value={insightsType}
                  options={insightOptions}
                  onChange={selection => setInsightsType(selection)}
                />
                <div>by DMA</div>
              </>
            }
            viewRightActions
          >
            {chartTypeView === 'showMap' && (
              <div className='geo-map-container'>
                <div className='geo-gradient-bar-and-map'>
                  {!displayBestAndWorst && (
                    <GradientLegend
                      bins={thresholds}
                      useConvergent={false}
                      legendStyle={legendStyle}
                      formatLabel={gradientLegendFormatLabel}
                      reverse={displayCpa}
                    />
                  )}
                  <div className='geo-map'>
                    <AutoSizer
                      renderProp={({ width, height }) => (
                        <DmaMap
                          width={Math.max(width ?? 0, GEO_MAP_MIN_WIDTH)}
                          height={Math.max(height ?? 0, GEO_MAP_MIN_HEIGHT)}
                          renderColor={renderColor}
                          renderTooltip={renderToolTip}
                          ref={mapSvgRef}
                          borderColor='ffffff'
                          // TODO: remove this after the demo on 10/28.
                          enableZoom={company === DMA_MAP_DEMO_COMPANY}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className='geo-filter-and-legend'>
                  {!displayBestAndWorst && (
                    <div className='geo-filter'>
                      <FilterCheckbox
                        filterOptions={filterOptions}
                        segments={filterOptions}
                        selectedSegments={selectedSegments}
                        setSelectedSegments={setSelectedSegments}
                      />
                    </div>
                  )}
                  {displayBestAndWorst && (
                    <div className='legend-row'>
                      <BestAndWorstLegend
                        label={INSIGHT_LABELS[insightsType].legend}
                        data={bestTenRanked}
                        dataFormatter={DATA_FORMATTERS[insightsType]}
                        geoNames={geoNames}
                        isBest
                      />
                      <BestAndWorstLegend
                        label={INSIGHT_LABELS[insightsType].legend}
                        data={worstTenRanked}
                        dataFormatter={DATA_FORMATTERS[insightsType]}
                        geoNames={geoNames}
                        isBest={false}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {chartTypeView === 'showTable' && (
              <div className='geo-table'>
                <BPMTable
                  data={Object.values(attributionByGeoData).map(item => ({
                    dma: item.dma,
                    fullName: item.fullName,
                    incrementalOutcome: DATA_FORMATTERS[insightsType](
                      item.incrementalOutcome
                    ) as string,
                    spend: currencyFormatter.format(item.spend, 2),
                    response: DATA_FORMATTERS[insightsType](item.response) as string
                  }))}
                  headers={tableHeaders}
                  filterBar={false}
                />
              </div>
            )}
          </ChartContainer>
        </div>
      </WidgetContainer>
    </div>
  );
};
